import { useEffect, useState, type ReactNode } from 'react';
import { daysUntil, fmtDateTime, fmtRelative } from '../lib/format';
import { useT } from '../i18n';

export function Spinner() {
  const t = useT();
  return <span className="spinner" aria-label={t('common.loading')} />;
}

export function Loading() {
  return (
    <div className="center-page">
      <Spinner />
    </div>
  );
}

export function ErrorAlert({ error }: { error: string | null | undefined }) {
  if (!error) return null;
  return (
    <div className="alert error" role="alert">
      {error}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, crumb }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; crumb?: ReactNode }) {
  return (
    <div className="page-header">
      <div>
        {crumb && <div className="breadcrumb">{crumb}</div>}
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {actions && <div className="btn-row">{actions}</div>}
    </div>
  );
}

export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const t = useT();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <header>
          <h2>{title}</h2>
          <button className="close" onClick={onClose} aria-label={t('common.close')}>
            ×
          </button>
        </header>
        <div className="body">{children}</div>
      </div>
    </div>
  );
}

export function Field({ label, help, children, full }: { label: string; help?: string; children: ReactNode; full?: boolean }) {
  return (
    <label className={`field${full ? ' full' : ''}`}>
      {label}
      {children}
      {help && <span className="help">{help}</span>}
    </label>
  );
}

const STATUS_TONE: Record<string, string> = { active: 'success', suspended: 'warning', revoked: 'danger', expired: 'danger' };

export function LicenseStatusBadge({ status, expiresAt }: { status: string; expiresAt?: string | null }) {
  const t = useT();
  const days = daysUntil(expiresAt);
  if (status === 'active' && days !== null && days <= 30) {
    return <span className="badge warning">{t('license.activeDays', { days })}</span>;
  }
  const label = STATUS_TONE[status] ? t(`license.status.${status as 'active'}`) : status;
  return <span className={`badge ${STATUS_TONE[status] ?? ''}`}>{label}</span>;
}

/**
 * Última conexión de una licencia: relativa (la fecha exacta al pasar el ratón) y en naranja a partir
 * de `warnDays`; «Nunca» si no se ha activado y «Por fichero» si solo tiene equipos sin conexión.
 */
export function LastSeen({ at, offlineOnly, warnDays = 30 }: { at?: string | null; offlineOnly?: boolean; warnDays?: number }) {
  const t = useT();
  if (at === undefined) return <span className="muted">—</span>;
  if (offlineOnly) {
    return <span className="badge info plain" title={t('lastSeen.offlineHelp', { date: fmtDateTime(at) })}>{t('lastSeen.offline')}</span>;
  }
  if (!at) return <span className="muted">{t('lastSeen.never')}</span>;
  const days = -(daysUntil(at) ?? 0);
  return (
    <span title={fmtDateTime(at)} style={days >= warnDays ? { color: 'var(--warning)' } : undefined}>
      {fmtRelative(at)}
    </span>
  );
}

export function TypeBadge({ type, mode, seats }: { type: string; mode: string; seats?: number }) {
  const t = useT();
  return (
    <span className="muted">
      {typeLabel(t, type)}
      {' · '}
      {modeLabel(t, mode)}
      {seats !== undefined && ` (${seats})`}
    </span>
  );
}

type T = ReturnType<typeof useT>;
export const typeLabel = (t: T, type: string) =>
  ['perpetual', 'subscription', 'trial'].includes(type) ? t(`license.type.${type as 'trial'}`) : type;
export const modeLabel = (t: T, mode: string) =>
  ['node_locked', 'floating'].includes(mode) ? t(`license.mode.${mode as 'floating'}`) : mode;

export function Pagination({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage: (p: number) => void }) {
  const t = useT();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="pagination">
      <span>
        {total === 0
          ? t('common.noResults')
          : t('common.range', { from: (page - 1) * pageSize + 1, to: Math.min(page * pageSize, total), total })}
      </span>
      <div className="btn-row">
        <button className="btn btn-sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          {t('common.previous')}
        </button>
        <button className="btn btn-sm" disabled={page >= pages} onClick={() => onPage(page + 1)}>
          {t('common.next')}
        </button>
      </div>
    </div>
  );
}

/** Botón que ejecuta una acción asíncrona con confirmación opcional y estado de carga. */
export function ActionButton({
  children,
  onAction,
  confirm,
  className = 'btn',
  disabled,
}: {
  children: ReactNode;
  onAction: () => Promise<unknown>;
  confirm?: string;
  className?: string;
  disabled?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      className={className}
      disabled={busy || disabled}
      onClick={async () => {
        if (confirm && !window.confirm(confirm)) return;
        setBusy(true);
        try {
          await onAction();
        } finally {
          setBusy(false);
        }
      }}
    >
      {busy ? <Spinner /> : null}
      {children}
    </button>
  );
}

export function CopyButton({ text }: { text: string }) {
  const t = useT();
  const [done, setDone] = useState(false);
  return (
    <button
      className="btn btn-sm"
      onClick={async () => {
        await navigator.clipboard.writeText(text);
        setDone(true);
        setTimeout(() => setDone(false), 1500);
      }}
    >
      {done ? t('common.copied') : t('common.copy')}
    </button>
  );
}
